import * as React from 'react';
import Modal from '@mui/material/Modal';
import { useSelector } from 'react-redux';
import FontAwesomeIcon from 'react-fontawesome';
import { CLOUDINARY_BASE_URL } from './Constants';
import { EventTypeTranslation, Store, TableEvent, TableEventType } from './types';
import { getTableCode } from './helpers';
import * as Api from './Api';

const getTableEventImg = (eventType: TableEventType): JSX.Element => {
    let img: string = null;

    switch (eventType) {
        case TableEventType.REQUEST_CASH_PAYOUT:
            img = 'cashPayout.png';
            break;
        case TableEventType.REQUEST_CREDIT_CARD_PAYOUT:
            img = 'creditCardPayout.png';
            break;
        case TableEventType.REQUEST_PERSONEL:
            img = 'personel.png';
            break;
        default:
            break;
    }

    return (
        img && (
            <img
                src={`${CLOUDINARY_BASE_URL}/generic/tableEvents/${img}`}
                className='event-img'
            />
        )
    );
};

const TableEvents: React.FC = () => {
    const mounted = React.useRef(false);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [selectedTableEventId, setSelectedTableEventId] =
        React.useState<number>(null);

    const tableEvents = useSelector(({ app }: Store) => app.tableEvents);
    const tables = useSelector(({ app }: Store) => app.tables);

    React.useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    const handleDialogClose = React.useCallback(() => {
        setModalOpen(false);
        setSelectedTableEventId(null);
    }, []);

    const handleSetTableEventSeen = React.useCallback(
        (id: number) => () => {
            Api.setEventTableSeen(id).then(() => {
                if (!mounted.current) {
                    return;
                }

                handleDialogClose();
            });
        },
        [handleDialogClose]
    );

    const renderFullTableEvent = React.useCallback(
        ({ tableID, eventType, comments, id, totalPriceToBePaid }: TableEvent) => (
            <div className='event-modal container modal'>
                <div className='table-id'>
                    <div>Table {getTableCode(tables, tableID)}</div>
                    <FontAwesomeIcon
                        name='times-circle'
                        className='close-icon'
                        onClick={handleDialogClose}
                    />
                </div>
                <div className='type'>
                    <div className='event-type'>
                        <label>ΕΙΔΟΣ ΚΛΗΣΗΣ:</label>
                        <div>{EventTypeTranslation[TableEventType[eventType]]}.</div>
                    </div>
                    {getTableEventImg(eventType)}
                </div>
                {comments && (
                    <div className='comments'>
                        <label>ΣΧΟΛΙΑ:</label>
                        {comments}
                    </div>
                )}
                {totalPriceToBePaid && (
                    <div className='total-amount'>
                        <label>ΓΕΝΙΚΟ ΣΥΝΟΛΟ:</label>
                        <div className='amount'>{totalPriceToBePaid} &euro;</div>
                    </div>
                )}
                <div className='actions'>
                    <button type='button' onClick={handleSetTableEventSeen(id)}>
                        ΑΠΟΔΟΧΗ
                    </button>
                </div>
            </div>
        ),
        [handleDialogClose, handleSetTableEventSeen, tables]
    );

    const handleTableEventClick = React.useCallback(
        (tableEventId: number) => () => {
            setModalOpen(true);
            setSelectedTableEventId(tableEventId);
        },
        []
    );

    return (
        <div className='table-events'>
            <div className='header'>
                <img src={`${CLOUDINARY_BASE_URL}/generic/tableEvents/header.png`} />
            </div>
            {tableEvents.map((tableEvent: TableEvent) => (
                <React.Fragment key={tableEvent.id}>
                    <div
                        className='event'
                        onClick={handleTableEventClick(tableEvent.id)}>
                        {getTableEventImg(tableEvent.eventType)}
                        <img
                            src={`${CLOUDINARY_BASE_URL}/generic/tableEvents/arrow.png`}
                            className='arrow'
                        />
                        <div className='table-id'>
                            {getTableCode(tables, tableEvent.tableID)}
                        </div>
                    </div>
                    <Modal
                        open={modalOpen && selectedTableEventId === tableEvent.id}
                        onClose={handleDialogClose}
                        BackdropProps={{ classes: { root: 'backdrop' } }}
                        classes={{ root: 'modal-dialog' }}>
                        {renderFullTableEvent(tableEvent)}
                    </Modal>
                </React.Fragment>
            ))}
        </div>
    );
};

export default TableEvents;
